import { useEffect, useState } from "react";
import { createRole, deleteRole, fetchRoles, updateRole } from "../../services/roleApi";
import { showErrorToast, showSuccessToast } from "../../utils/toast";
import ConfirmationModal from "../../components/CommonComponents/ConfirmationModal";
import { Trash2 } from "lucide-react";
import { MdToggleOn, MdToggleOff } from "react-icons/md";

export default function CustomerDetailsTab() {
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [roleName, setRoleName] = useState("");
    const [confirmState, setConfirmState] = useState({ isOpen: false, roleId: null, roleName: "" });

    const loadRoles = async () => {
        setLoading(true);
        try {
            const res = await fetchRoles();
            setRoles(res.data.data || []);
        } catch (err) {
            console.error("Failed to fetch roles:", err);
            showErrorToast("Failed to load roles.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadRoles();
    }, []);

    const handleAddRole = async (e) => {
        e.preventDefault();
        if (!roleName.trim()) return;
        try {
            await createRole({ name: roleName.trim() });
            setRoleName("");
            showSuccessToast("Role added.");
            loadRoles();
        } catch (err) {
            console.error("Failed to add role:", err);
            showErrorToast(err.response?.data?.message || "Failed to add role.");
        }
    };

    const handleDeleteRole = async (id) => {
        try {
            await deleteRole(id);
            showSuccessToast("Role deleted.");
            loadRoles();
        } catch (err) {
            console.error("Failed to delete role:", err);
            showErrorToast(err.response?.data?.message || "Failed to delete role.");
        }
    };

    const toggleRoleStatus = async (role) => {
        const nextStatus = role.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
        try {
            await updateRole(role.id, { name: role.name, status: nextStatus });
            showSuccessToast(`Role ${nextStatus === "ACTIVE" ? "activated" : "deactivated"}.`);
            loadRoles();
        } catch (err) {
            console.error("Failed to update role:", err);
            showErrorToast(err.response?.data?.message || "Failed to update role.");
        }
    };

    const openDeleteConfirm = (role) => {
        setConfirmState({ isOpen: true, roleId: role.id, roleName: role.name });
    };

    const closeDeleteConfirm = () => {
        setConfirmState({ isOpen: false, roleId: null, roleName: "" });
    };

    const confirmDelete = async () => {
        if (!confirmState.roleId) return;
        await handleDeleteRole(confirmState.roleId);
        closeDeleteConfirm();
    };

    return (
        <div className="bg-white rounded-xl shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Customer Details</h2>
            <form onSubmit={handleAddRole} className="flex flex-wrap gap-3 items-end mb-6">
                <div className="flex flex-col min-w-[220px]">
                    <label className="text-sm font-semibold text-gray-700 mb-1">New Role</label>
                    <input
                        type="text"
                        value={roleName}
                        onChange={(e) => setRoleName(e.target.value)}
                        placeholder="e.g., Supervisor"
                        className="border rounded-lg px-3 py-2 text-sm"
                    />
                </div>
                <button
                    type="submit"
                    className="px-4 py-2 bg-black text-white rounded-lg"
                >Add Role</button>
            </form>

            {loading ? (
                <div className="text-sm text-gray-500">Loading roles...</div>
            ) : roles.length === 0 ? (
                <div className="text-sm text-gray-500">No roles added yet.</div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                        <thead>
                            <tr>
                                <th className="px-4 py-2 text-left">Role</th>
                                <th className="px-4 py-2 text-left">Status</th>
                                <th className="px-4 py-2 text-left">Created</th>
                                <th className="px-4 py-2 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {roles.map(role => (
                                <tr key={role.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-2">{role.name}</td>
                                    <td className="px-4 py-2">
                                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${role.status === "ACTIVE"
                                            ? "bg-green-100 text-green-700"
                                            : role.status === "INACTIVE"
                                                ? "bg-yellow-100 text-yellow-700"
                                                : "bg-gray-200 text-gray-600"}`}
                                        >{role.status || "ACTIVE"}</span>
                                    </td>
                                    <td className="px-4 py-2">{role.createdAt ? new Date(role.createdAt).toLocaleString() : "-"}</td>
                                    <td className="px-4 py-2 text-right">
                                        <div className="flex items-center justify-end gap-3">
                                            <button
                                                type="button"
                                                onClick={() => toggleRoleStatus(role)}
                                                title={role.status === "ACTIVE" ? "Deactivate" : "Activate"}
                                                className="transition"
                                            >
                                                {role.status === "ACTIVE" ? (
                                                    <MdToggleOn size={30} className="text-green-500" />
                                                ) : (
                                                    <MdToggleOff size={30} className="text-gray-400" />
                                                )}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => openDeleteConfirm(role)}
                                                className="transition-colors p-1 rounded hover:bg-red-50"
                                                title="Delete role"
                                                style={{ color: "#6b7280" }}
                                                onMouseOver={e => e.currentTarget.style.color = '#dc2626'}
                                                onMouseOut={e => e.currentTarget.style.color = '#6b7280'}
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <ConfirmationModal
                isOpen={confirmState.isOpen}
                onClose={closeDeleteConfirm}
                onConfirm={confirmDelete}
                title="Delete role?"
                message={confirmState.roleName ? `Delete ${confirmState.roleName}?` : "Delete this role?"}
                confirmText="Delete"
                cancelText="Cancel"
                type="delete"
            />
        </div>
    );
}